import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenant } from '../context/TenantContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { GraduationCap, ChevronLeft, AlertTriangle, Check, Sparkles, X, Home, ArrowLeft, LogIn, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STUDENT_PLAN_DEFAULT = {
  id: 'plus',
  name: 'Plus',
  priceMonthly: 20,
  priceAnnual: 200,
  annualSavings: '2 months free',
  features: [
    'Personality Quiz & career matching',
    'Lifestyle Calculator, design your dream life',
    'Decision Lab, compare career paths',
    'Scholarship Finder (AI-matched)',
    'Future Roadmap & FuturePath Report',
    'College Explorer & exam prep',
  ],
};

const PROFESSIONAL_PLAN_DEFAULT = {
  id: 'pro',
  name: 'Pro',
  priceMonthly: 40,
  priceAnnual: 400,
  annualSavings: '2 months free',
  features: [
    'Career Assessment & skills gap analysis',
    'Decision Lab, compare career pivots',
    'Lifestyle Calculator, model target salary',
    'Upskilling paths & certifications',
    'Resume ATS analysis',
    'FuturePath Report with action plan',
  ],
};

function CancelDialog({ open, onClose, navigate }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a1729]/70 backdrop-blur-md p-4 animate-in fade-in duration-200" data-testid="cancel-dialog" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl overflow-hidden bg-gradient-to-b from-slate-900/95 to-slate-950/95 border border-slate-700/60 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)]"
      >
        {/* Decorative top accent bar */}
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-emerald-500 via-amber-500 to-blue-500" />

        <div className="p-7 space-y-6">
          {/* Brand Lockup */}
          <div className="flex items-center justify-center gap-2.5">
            <img src="/fp-logo.png" alt="Agentic FuturePath AI" className="h-9 w-9 rounded-lg shadow-[0_0_16px_rgba(59,130,246,0.35)]" />
            <span className="font-outfit font-bold text-white text-base tracking-tight">
              <span className="text-cyan-300">AGENTIC</span> FUTUREPATH <span className="text-blue-400">AI</span>
            </span>
          </div>

          <div className="text-center">
            <h3 className="font-outfit text-xl font-semibold text-white mb-1.5">Where would you like to go?</h3>
            <p className="text-sm text-slate-300">Your progress will not be saved if you leave.</p>
          </div>

          <div className="space-y-2.5">
            <button
              onClick={() => navigate('/')}
              className="group w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-slate-800/60 hover:bg-emerald-500/10 border border-slate-700/60 hover:border-emerald-500/40 text-slate-200 hover:text-white text-sm font-medium transition-all"
              data-testid="cancel-go-home"
            >
              <div className="w-9 h-9 rounded-lg bg-emerald-500/15 flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-500/25 transition-colors">
                <Home className="w-4 h-4 text-emerald-400" />
              </div>
              <span className="flex-1 text-left">Go to Homepage</span>
              <ArrowLeft className="w-4 h-4 text-slate-300 rotate-180 group-hover:text-emerald-400 group-hover:translate-x-0.5 transition-all" />
            </button>

            <button
              onClick={() => navigate('/pricing')}
              className="group w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-slate-800/60 hover:bg-amber-500/10 border border-slate-700/60 hover:border-amber-500/40 text-slate-200 hover:text-white text-sm font-medium transition-all"
              data-testid="cancel-go-plans"
            >
              <div className="w-9 h-9 rounded-lg bg-amber-500/15 flex items-center justify-center flex-shrink-0 group-hover:bg-amber-500/25 transition-colors">
                <ArrowLeft className="w-4 h-4 text-amber-400" />
              </div>
              <span className="flex-1 text-left">Back to plans</span>
              <ArrowLeft className="w-4 h-4 text-slate-300 rotate-180 group-hover:text-amber-400 group-hover:translate-x-0.5 transition-all" />
            </button>

            <button
              onClick={() => navigate('/login')}
              className="group w-full flex items-center gap-3 px-4 py-3.5 rounded-xl bg-slate-800/60 hover:bg-blue-500/10 border border-slate-700/60 hover:border-blue-500/40 text-slate-200 hover:text-white text-sm font-medium transition-all"
              data-testid="cancel-go-login"
            >
              <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-500/25 transition-colors">
                <LogIn className="w-4 h-4 text-blue-400" />
              </div>
              <span className="flex-1 text-left">Go to Login</span>
              <ArrowLeft className="w-4 h-4 text-slate-300 rotate-180 group-hover:text-blue-400 group-hover:translate-x-0.5 transition-all" />
            </button>
          </div>

          <button
            onClick={onClose}
            className="w-full py-3 rounded-full text-sm font-medium text-slate-300 hover:text-white bg-slate-800/40 hover:bg-slate-700/60 border border-slate-700/40 transition-colors"
            data-testid="cancel-stay"
          >
            Stay on this page
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdultStudentRegistration() {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const { branding } = useTenant();
  const [searchParams] = useSearchParams();
  const tenant = searchParams.get('tenant') || '';

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [dob, setDob] = useState('');
  const [gradeLevel, setGradeLevel] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showCancel, setShowCancel] = useState(false);

  // Plan selection
  const planParam = searchParams.get('plan') || 'plus';
  const [selectedPlan, setSelectedPlan] = useState(planParam);
  const [billingCycle, setBillingCycle] = useState(() => {
    // Respect the Monthly/Yearly choice the user made on the Pricing page
    const fromQuery = searchParams.get('interval') || searchParams.get('billing');
    return fromQuery === 'annual' || fromQuery === 'monthly' ? fromQuery : 'monthly';
  });
  const isProfessional = selectedPlan === 'pro';
  const defaultPlan = isProfessional ? PROFESSIONAL_PLAN_DEFAULT : STUDENT_PLAN_DEFAULT;
  const [STUDENT_PLAN, setStudentPlan] = useState(defaultPlan);

  // Honour whatever plan they picked on pricing; fall back to the entry plan.
  useEffect(() => {
    axios.get(`${API}/plans`).then(res => {
      const plans = (res.data.plans || []).filter(p => p.key !== 'trial');
      const picked = plans.find(p => p.key === selectedPlan) || plans.find(p => p.key === 'plus');
      if (picked) setStudentPlan(prev => ({ ...prev, name: picked.name,
        priceMonthly: picked.price_monthly, priceAnnual: picked.price_annual,
        credits: picked.monthly_credits }));
    }).catch(() => {});
  }, [selectedPlan]);

  const price = billingCycle === 'annual' ? STUDENT_PLAN.priceAnnual : STUDENT_PLAN.priceMonthly;
  const pricePeriod = billingCycle === 'annual' ? '/yr' : '/mo';

  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    const trimmed = (email || '').trim();
    // Strong email format check, requires user@domain.tld with TLD >= 2 chars
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!trimmed) {
      setError('Email address is required'); return;
    }
    if (!emailRegex.test(trimmed)) {
      setError('Please enter a valid email address (example: name@domain.com)'); return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/register/start`, {
        email: trimmed,
        plan: selectedPlan,
        billing_period: billingCycle,
        num_children: 0,
        role: isProfessional ? 'professional' : 'student',
      });

      if (res.data.success && res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        setError('Failed to create payment session. Please try again.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {registrationComplete ? (
          /* Email Verification Screen */
          <div className="text-center" data-testid="verify-email-screen">
            <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-5">
              <Check className="w-8 h-8 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Check your email</h1>
            <p className="text-slate-300 text-sm mb-6">
              We sent a verification link to <strong className="text-white">{registeredEmail}</strong>
            </p>
            <div className="glass-card p-5 border border-slate-700 text-left mb-6 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-blue-400 text-xs font-bold">1</span>
                </div>
                <p className="text-slate-300 text-sm">Open the email from <strong>Agentic FuturePath AI</strong></p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-blue-400 text-xs font-bold">2</span>
                </div>
                <p className="text-slate-300 text-sm">Click <strong>"Verify My Email"</strong> to confirm your account</p>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                  <span className="text-blue-400 text-xs font-bold">3</span>
                </div>
                <p className="text-slate-300 text-sm">You'll be redirected to <strong>sign in</strong> and set up your plan</p>
              </div>
            </div>
            <p className="text-slate-300 text-xs mb-4">Didn't receive it? Check your spam folder or wait a minute.</p>
            <button onClick={() => navigate('/login')} className="text-blue-400 hover:underline text-sm">
              Go to Sign In
            </button>
          </div>
        ) : (
        <>
        <div className="text-center mb-6 relative">
          <button
            onClick={() => setShowCancel(true)}
            className="absolute right-0 top-0 p-2 rounded-lg text-slate-300 hover:text-white hover:bg-slate-700/50 transition-colors"
            data-testid="cancel-registration-btn"
          >
            <X className="w-5 h-5" />
          </button>
          <div className={`w-14 h-14 rounded-xl ${isProfessional ? 'bg-amber-500/10' : 'bg-emerald-500/10'} flex items-center justify-center mx-auto mb-4`}>
            {isProfessional ? <Briefcase className="w-7 h-7 text-amber-400" /> : <GraduationCap className="w-7 h-7 text-emerald-400" />}
          </div>
          <h1 className="text-2xl font-bold text-white">Create your account</h1>
          <p className="text-slate-300 text-sm mt-1">
            FuturePath {STUDENT_PLAN.name}
            {STUDENT_PLAN.credits ? ` · ${STUDENT_PLAN.credits.toLocaleString()} AI credits a month` : ''}
          </p>
        </div>

        {/* Plan selection card */}
        <div className="glass-card p-4 border border-slate-700 mb-4" data-testid="student-plan-card">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Sparkles className={`w-4 h-4 ${isProfessional ? 'text-amber-400' : 'text-emerald-400'}`} />
              <span className="font-semibold text-white text-sm">FuturePath {STUDENT_PLAN.name}</span>
            </div>
            <div className="relative inline-grid grid-cols-2 bg-slate-800 border border-slate-700 rounded-full p-0.5 select-none" role="tablist" data-testid="billing-toggle">
              <span
                aria-hidden="true"
                className={`absolute top-0.5 bottom-0.5 left-0.5 w-[calc(50%-2px)] rounded-full ${isProfessional ? 'bg-amber-500' : 'bg-emerald-500'} transition-transform duration-200 ease-out ${billingCycle === 'annual' ? 'translate-x-full' : 'translate-x-0'}`}
              />
              <button
                type="button"
                onClick={() => setBillingCycle('monthly')}
                aria-pressed={billingCycle === 'monthly'}
                className={`relative z-10 px-3 py-0.5 text-xs font-medium rounded-full transition-colors ${billingCycle === 'monthly' ? 'text-white' : 'text-slate-300'}`}
                data-testid="billing-toggle-monthly"
              >
                Mo
              </button>
              <button
                type="button"
                onClick={() => setBillingCycle('annual')}
                aria-pressed={billingCycle === 'annual'}
                className={`relative z-10 px-3 py-0.5 text-xs font-medium rounded-full transition-colors ${billingCycle === 'annual' ? 'text-white' : 'text-slate-300'}`}
                data-testid="billing-toggle-annual"
              >
                Yr
              </button>
            </div>
          </div>
          <div className="flex items-baseline gap-1 mb-3">
            <span className="text-xl font-bold text-white">${price}</span>
            <span className="text-slate-300 text-xs">{pricePeriod}</span>
            <span className="text-emerald-400 text-xs ml-1">Full access</span>
          </div>
          <div className="grid grid-cols-2 gap-1">
            {STUDENT_PLAN.features.map((f, i) => (
              <span key={i} className="text-xs text-slate-300 flex items-center gap-1.5">
                <Check className="w-3 h-3 text-emerald-400 shrink-0" /> {f}
              </span>
            ))}
          </div>
          <p className="text-xs text-slate-300 mt-2">
            Or <button onClick={() => { setSelectedPlan('plus'); }} className="text-blue-400 hover:underline" data-testid="free-plan-link">see what's included</button> to explore all features.
          </p>
        </div>

        <form onSubmit={handleRegister} className="glass-card p-6 border border-slate-700 space-y-4" data-testid="adult-student-form">
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm flex items-center gap-2" data-testid="adult-registration-error">
              <AlertTriangle className="w-4 h-4 shrink-0" /> {error}
            </div>
          )}

          <div>
            <label className="text-sm text-slate-300 block mb-1">Email Address</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" data-testid="adult-email-input" />
            <p className="text-xs text-slate-300 mt-1">You'll create your name and password after payment</p>
          </div>

          <Button type="submit" disabled={loading} className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 py-4" data-testid="adult-register-btn">
            {loading ? 'Redirecting to payment...' : `Continue to Payment, $${price}${pricePeriod}`}
          </Button>

          <p className="text-center text-slate-300 text-xs">Secure payment via Stripe. Set up your account after payment.</p>
          <p className="text-center text-slate-300 text-xs">
            Signing up to guide your child?{' '}
            <button type="button" data-testid="parent-signup-link"
              onClick={() => navigate(`/register/parent?plan=${selectedPlan}&interval=${billingCycle}`)}
              className="text-blue-400 hover:underline">Use the parent setup instead</button>
          </p>
          <div className="flex items-center justify-between pt-2">
            <button type="button" onClick={() => navigate('/pricing')} className="text-slate-300 text-sm hover:text-white flex items-center gap-1">
              <ChevronLeft className="w-4 h-4" /> Back
            </button>
            <span className="text-slate-300 text-sm">Have an account? <button type="button" onClick={() => navigate('/login')} className="text-blue-400 hover:underline">Sign in</button></span>
          </div>
        </form>
        </>
        )}
      </div>
      <CancelDialog open={showCancel} onClose={() => setShowCancel(false)} navigate={navigate} />
    </div>
  );
}
